import React from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_MY_FAVORITES, TOGGLE_FAVORITE } from '../graphql/voting';
import { formatDate } from '../utils/dateUtils';

const FavoriteVoting = () => {
  const navigate = useNavigate();

  const { loading, error, data, refetch } = useQuery(GET_MY_FAVORITES, {
    fetchPolicy: 'cache-and-network'
  });

  const [toggleFavorite] = useMutation(TOGGLE_FAVORITE, {
    onCompleted: (data) => {
      if (data.toggleFavorite.success) {
        refetch();
      }
    },
    onError: (error) => {
      console.error('取消收藏失敗:', error);
      alert('取消收藏失敗：' + error.message);
    }
  });

  const handleUnfavorite = async (articleId) => {
    try {
      await toggleFavorite({
        variables: { articleId }
      });
    } catch (error) {
      console.error('取消收藏錯誤:', error);
    }
  };

  const favorites = data?.myFavorites?.favorites || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">載入失敗</div>
          <button 
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 標題 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">我的收藏</h1>
          <p className="text-gray-600">你收藏的投票列表</p>
        </div>

        {/* 統計資訊 */}
        <div className="mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {favorites.length}
                </div>
                <div className="text-sm text-gray-600">收藏數量</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {favorites.filter(f => f.article.status === 'PUBLISHED').length}
                </div>
                <div className="text-sm text-gray-600">進行中</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {favorites.reduce((sum, f) => sum + (f.article.voteCount || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">總參與人數</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {favorites.reduce((sum, f) => sum + (f.article.commentCount || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">總評論數</div>
              </div>
            </div>
          </div>
        </div>

        {/* 收藏列表 */}
        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">你還沒有收藏任何投票</div>
            <button 
              onClick={() => navigate('/voting')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              去發現投票
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((favorite) => {
              const article = favorite.article;
              return (
                <div key={favorite.id} className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {article.question?.title || '未知問題'}
                      </h3>
                      <p className="text-gray-600 mb-3">
                        {article.question?.description || ''}
                      </p>
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        <span>🆔 {article.id}</span>
                        {article.creator?.id ? (
                          <span
                            onClick={() => navigate(`/user/${article.creator.id}`)}
                            className="cursor-pointer hover:text-blue-600 hover:underline transition-colors"
                          >
                            👤 {article.creator.username || '匿名'}
                          </span>
                        ) : (
                          <span>👤 {article.creator?.username || '匿名'}</span>
                        )}
                        <span>📅 {formatDate(article.createdAt)}</span>
                        <span>🗳️ {article.voteCount || 0} 票</span>
                        <span>💬 {article.commentCount || 0} 評論</span>
                        <span>👁️ {article.viewCount || 0} 瀏覽</span>
                        <span>⭐ 收藏於 {formatDate(favorite.favoritedAt)}</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        article.status === 'PUBLISHED' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {article.status === 'PUBLISHED' ? '進行中' : '已結束'}
                      </span>
                    </div>
                  </div>
                  
                  {/* 問題預覽 */}
                  {article.question && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-800 mb-2">
                        {article.question.title}
                      </h4>
                      <div className="text-sm text-gray-600">
                        {article.question.options?.slice(0, 2).map((option, index) => (
                          <span key={index} className="mr-3">
                            {option.text}
                          </span>
                        ))}
                        {article.question.options?.length > 2 && (
                          <span className="text-gray-500">...等 {article.question.options.length} 個選項</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/voting/${article.id}`)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        查看詳情
                      </button>
                      {article.creatorId === article.creator?.id && (
                        <button
                          onClick={() => navigate(`/voting/${article.id}`)}
                          className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          管理投票
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleUnfavorite(article.id)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      ❌ 取消收藏
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoriteVoting; 